import { __ } from '../../utils';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Erb,
  IconButton,
  Stack,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  title: string;
  /** 0 - 1. `undefined` znamená, že pásik postupu sa nekreslí vôbec. */
  progress?: number;
  onLeave: () => void;
  /** Odkaz na priebežné výsledky - len keď ich hlasovanie zverejňuje. */
  resultsAction?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * Rám sústredeného režimu: hlavička s erbom a názvom, pásik postupu, obsah
 * a pripnutá pätička.
 *
 * Je to celá plocha okna, nie vrstva nad shellom - shell sa nad touto adresou
 * vôbec nekreslí ([[Layout]] podľa `isVotingRunPath`). Dôvod je ten istý ako
 * pri platobnej bráne: kým je otvorený lístok, nemá byť kam odbočiť. Odchod je
 * jediný a menuje sa „Prerušiť".
 */
const VotingRunChrome: React.FC<Props> = ({
  title,
  progress,
  onLeave,
  resultsAction,
  footer,
  children,
}) => (
  <Stack
    sx={({ palette }) => ({
      position: 'fixed',
      inset: 0,
      zIndex: 1200,
      backgroundColor: palette.background.paper,
    })}
  >
    <Stack
      direction='row'
      alignItems='center'
      gap={{ xs: 1.25, md: 1.5 }}
      sx={({ palette }) => ({
        flex: 'none',
        height: { xs: 52, md: 56 },
        px: { xs: 1.75, md: 2.5 },
        borderBottom: `1px solid ${palette.divider}`,
      })}
    >
      <Erb variant='primary' size={24} title={null} />

      <Box
        component='b'
        sx={{
          fontSize: { xs: 13, md: 14 },
          fontWeight: 700,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={title}
      >
        {title}
      </Box>

      <Box
        component='span'
        sx={{
          flex: 'none',
          fontSize: 13,
          color: 'text.secondary',
          display: { xs: 'none', md: 'inline' },
        }}
      >
        {__('· hlasovanie mesta Prešov')}
      </Box>

      <Stack
        direction='row'
        alignItems='center'
        gap={2}
        sx={{ ml: 'auto', flex: 'none' }}
      >
        {resultsAction}
        <Button
          variant='outline'
          size='small'
          onClick={onLeave}
          endIcon={<CloseIcon fontSize='small' />}
          sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
        >
          {__('Prerušiť')}
        </Button>
        <IconButton
          aria-label={__('Prerušiť')}
          onClick={onLeave}
          sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>
    </Stack>

    {progress !== undefined && (
      <Box
        sx={({ palette }) => ({
          flex: 'none',
          height: 3,
          backgroundColor: palette.grey[100],
        })}
      >
        <Box
          sx={({ palette }) => ({
            width: `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%`,
            height: 3,
            backgroundColor: palette.primary.main,
            transition: 'width 200ms ease',
          })}
        />
      </Box>
    )}

    <Stack sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Stack>

    {footer && (
      <Box
        sx={({ palette }) => ({
          flex: 'none',
          borderTop: `1px solid ${palette.divider}`,
          backgroundColor: palette.background.paper,
          px: { xs: 2, md: 2.5 },
          py: 1.5,
        })}
      >
        {footer}
      </Box>
    )}
  </Stack>
);

export default VotingRunChrome;
